import { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { usePasswordReset, useSignInFlow } from '@mystudioclass/auth';
import { Video, ArrowLeft, Mail, Lock, CheckCircle, Eye, EyeOff } from '../components/Icons';
import toast from 'react-hot-toast';

export default function ForgotPasswordPage() {
  const { isLoaded, sendResetCode, resetPassword, error } = usePasswordReset();
  const { createSignIn } = useSignInFlow();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Force light mode for public pages
  useEffect(() => {
    const previousTheme = document.documentElement.getAttribute('data-theme');
    document.documentElement.setAttribute('data-theme', 'light');
    return () => {
      if (previousTheme) {
        document.documentElement.setAttribute('data-theme', previousTheme);
      }
    };
  }, []);

  // Check if we have a reset token from the URL (user clicked link in email)
  const resetToken = searchParams.get('token');
  const emailFromUrl = searchParams.get('email');

  const [step, setStep] = useState(resetToken ? 'password' : 'email'); // 'email', 'sent', 'password'
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const handleSendResetEmail = async (e) => {
    e.preventDefault();
    if (!isLoaded || !email) return;

    setLoading(true);
    try {
      await sendResetCode(email);
      toast.success('Reset link sent! Check your email.');
      setStep('sent');
    } catch (err) {
      console.error('Send reset email error:', err);
      const errorMessage = err.message || error?.message || 'Failed to send reset email';
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    if (!isLoaded || !password || !resetToken) return;

    if (password !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    if (password.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }

    setLoading(true);
    try {
      await resetPassword(password, resetToken);
      toast.success('Password reset successfully!');

      // Auto sign-in with new password if we have the email
      if (emailFromUrl) {
        try {
          await createSignIn({ identifier: emailFromUrl, password });
          // Use hard navigation to avoid race condition with React auth state
          window.location.href = '/dashboard';
          return;
        } catch (signInErr) {
          console.error('Auto sign-in failed:', signInErr);
          // Fall through to login page if auto sign-in fails
        }
      }

      window.location.href = '/login';
    } catch (err) {
      console.error('Reset password error:', err);
      const errorMessage = err.message || error?.message || 'Failed to reset password';
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center px-4"
      style={{ background: 'var(--bg-base)' }}
    >
      <div className="max-w-md w-full">
        {/* Logo */}
        <div className="text-center mb-8">
          <div
            className="inline-flex items-center justify-center w-16 h-16 rounded-xl mb-4"
            style={{
              background: 'linear-gradient(135deg, var(--primary-hover), var(--primary))',
              boxShadow: '0 0 40px var(--primary-30)'
            }}
          >
            <Video className="w-8 h-8" style={{ color: 'var(--bg-base)' }} />
          </div>
          <h1 className="text-3xl font-bold" style={{ color: 'var(--text-primary)' }}>My Studio Class</h1>
          <p style={{ color: 'var(--text-muted)' }} className="mt-2">Reset Your Password</p>
        </div>

        {/* Form Card - Glass Effect */}
        <div
          className="rounded-2xl p-8 relative overflow-hidden"
          style={{
            background: 'linear-gradient(145deg, var(--bg-elevated) 0%, var(--bg-surface) 100%)',
            border: '1px solid var(--border-muted)',
            boxShadow: '0 8px 32px rgba(0,0,0,0.2), 0 0 60px var(--primary-10)'
          }}
        >
          {/* Top shine effect */}
          <div style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: 1,
            background: 'linear-gradient(90deg, transparent, var(--primary-30), var(--highlight-15), var(--primary-30), transparent)',
          }} />

          {/* Email Step - Request Reset */}
          {step === 'email' && (
            <form onSubmit={handleSendResetEmail} className="space-y-6">
              <div className="flex items-center justify-center mb-4">
                <div
                  className="flex items-center justify-center w-14 h-14 rounded-full"
                  style={{
                    background: 'linear-gradient(135deg, var(--primary-20) 0%, var(--primary-10) 100%)',
                    boxShadow: '0 0 20px var(--primary-15)'
                  }}
                >
                  <Mail className="w-7 h-7" style={{ color: 'var(--primary)' }} />
                </div>
              </div>

              <div className="text-center mb-4">
                <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
                  Enter your email address and we'll send you a link to reset your password.
                </p>
              </div>

              <div>
                <label className="label">Email Address</label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="input"
                  placeholder="customer@example.com"
                  required
                  disabled={!isLoaded || loading}
                />
              </div>

              <button
                type="submit"
                disabled={!isLoaded || loading}
                className="btn btn-primary w-full"
              >
                {loading ? 'Sending...' : 'Send Reset Link'}
              </button>
            </form>
          )}

          {/* Sent Step - Show Success */}
          {step === 'sent' && (
            <div className="space-y-6 text-center">
              <div className="flex items-center justify-center mb-4">
                <div
                  className="flex items-center justify-center w-14 h-14 rounded-full"
                  style={{
                    background: 'linear-gradient(135deg, var(--secondary-20) 0%, var(--secondary-10) 100%)',
                    boxShadow: '0 0 20px var(--secondary-15)'
                  }}
                >
                  <CheckCircle className="w-7 h-7" style={{ color: 'var(--secondary)' }} />
                </div>
              </div>

              <div>
                <h3 className="text-lg font-semibold mb-2" style={{ color: 'var(--text-primary)' }}>Check Your Email</h3>
                <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
                  We sent a password reset link to <strong style={{ color: 'var(--primary)' }}>{email}</strong>.
                  Click the link in the email to reset your password.
                </p>
              </div>

              <div className="pt-4" style={{ borderTop: '1px solid var(--border-muted)' }}>
                <p className="text-sm mb-4" style={{ color: 'var(--text-muted)' }}>
                  Didn't receive the email? Check your spam folder or try again.
                </p>
                <button
                  onClick={() => setStep('email')}
                  className="text-sm font-medium transition-colors"
                  style={{ color: 'var(--primary)' }}
                  onMouseOver={(e) => e.currentTarget.style.color = 'var(--primary-hover)'}
                  onMouseOut={(e) => e.currentTarget.style.color = 'var(--primary)'}
                >
                  Try again with different email
                </button>
              </div>
            </div>
          )}

          {/* Password Step - User Clicked Link in Email */}
          {step === 'password' && (
            <form onSubmit={handleResetPassword} className="space-y-6">
              <div className="flex items-center justify-center mb-4">
                <div
                  className="flex items-center justify-center w-14 h-14 rounded-full"
                  style={{
                    background: 'linear-gradient(135deg, var(--primary-20) 0%, var(--primary-10) 100%)',
                    boxShadow: '0 0 20px var(--primary-15)'
                  }}
                >
                  <Lock className="w-7 h-7" style={{ color: 'var(--primary)' }} />
                </div>
              </div>

              <div className="text-center mb-4">
                <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
                  Enter your new password below.
                </p>
              </div>

              <div>
                <label className="label">New Password</label>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="input pr-10"
                    placeholder="••••••••"
                    required
                    disabled={!isLoaded || loading}
                    minLength={8}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 transition-colors"
                    style={{ color: 'var(--text-muted)' }}
                    onMouseOver={(e) => e.currentTarget.style.color = 'var(--text-secondary)'}
                    onMouseOut={(e) => e.currentTarget.style.color = 'var(--text-muted)'}
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>

              <div>
                <label className="label">Confirm Password</label>
                <div className="relative">
                  <input
                    type={showConfirmPassword ? 'text' : 'password'}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="input pr-10"
                    placeholder="••••••••"
                    required
                    disabled={!isLoaded || loading}
                    minLength={8}
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 transition-colors"
                    style={{ color: 'var(--text-muted)' }}
                    onMouseOver={(e) => e.currentTarget.style.color = 'var(--text-secondary)'}
                    onMouseOut={(e) => e.currentTarget.style.color = 'var(--text-muted)'}
                  >
                    {showConfirmPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>

              <button
                type="submit"
                disabled={!isLoaded || loading}
                className="btn btn-primary w-full"
              >
                {loading ? 'Resetting...' : 'Reset Password'}
              </button>
            </form>
          )}

          {/* Back to Login */}
          <div className="mt-6 text-center">
            <Link
              to="/login"
              className="inline-flex items-center text-sm font-medium transition-colors"
              style={{ color: 'var(--primary)' }}
              onMouseOver={(e) => e.currentTarget.style.color = 'var(--primary-hover)'}
              onMouseOut={(e) => e.currentTarget.style.color = 'var(--primary)'}
            >
              <ArrowLeft className="w-4 h-4 mr-1" />
              Back to Sign In
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
